import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Music, Zap } from 'lucide-react';
import { MiniGameResult, PersonalityType } from '@/types/career-sim';
import MiniGameWrapper from './MiniGameWrapper';

interface PerformanceGameProps {
  onComplete: (result: MiniGameResult) => void;
  onExit: () => void;
  personality: PersonalityType;
}

type ButtonKey = 'a' | 's' | 'd' | 'f';

interface Prompt {
  id: string;
  key: ButtonKey;
  timing: number;
}

const PerformanceGame: React.FC<PerformanceGameProps> = ({ onComplete, onExit, personality }) => {
  const maxTime = 25;
  const [timeLeft, setTimeLeft] = useState(maxTime);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [activeKey, setActiveKey] = useState<ButtonKey | null>(null);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [crowdHype, setCrowdHype] = useState(50);
  const [totalPrompts, setTotalPrompts] = useState(0);
  const [hits, setHits] = useState(0);

  const keys: ButtonKey[] = ['a', 's', 'd', 'f'];
  const keyLabels = { a: 'A', s: 'S', d: 'D', f: 'F' };

  // Generate prompts
  useEffect(() => {
    const interval = setInterval(() => {
      const newPrompt: Prompt = {
        id: Math.random().toString(36),
        key: keys[Math.floor(Math.random() * keys.length)],
        timing: Date.now()
      };
      setPrompts(prev => [...prev, newPrompt]);
      setTotalPrompts(prev => prev + 1);
    }, 800);

    return () => clearInterval(interval);
  }, []);

  // Remove old prompts
  useEffect(() => {
    const interval = setInterval(() => {
      setPrompts(prev => {
        const now = Date.now();
        const filtered = prev.filter(p => now - p.timing < 2000);
        if (filtered.length < prev.length) {
          // Missed prompts
          setCombo(0);
          setCrowdHype(h => Math.max(0, h - 5));
        }
        return filtered;
      });
    }, 100);

    return () => clearInterval(interval);
  }, []);

  // Timer
  useEffect(() => {
    if (timeLeft <= 0) {
      endGame();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleKeyPress = useCallback((key: ButtonKey) => {
    setActiveKey(key);
    setTimeout(() => setActiveKey(null), 100);

    const matchingPrompt = prompts.find(p => p.key === key);
    if (matchingPrompt) {
      const now = Date.now();
      const timing = now - matchingPrompt.timing;
      
      // Perfect: < 500ms, Good: < 1000ms, Okay: < 1500ms
      let points = 10;
      if (timing < 500) points = 30;
      else if (timing < 1000) points = 20;
      else if (timing < 1500) points = 15;

      const newCombo = combo + 1;
      setCombo(newCombo);
      
      const comboMultiplier = Math.min(5, 1 + Math.floor(newCombo / 3));
      const finalPoints = points * comboMultiplier;
      
      setScore(s => s + finalPoints);
      setHits(h => h + 1);
      setCrowdHype(h => Math.min(100, h + 3));
      setPrompts(prev => prev.filter(p => p.id !== matchingPrompt.id));
    } else {
      // Missed
      setCombo(0);
      setCrowdHype(h => Math.max(0, h - 3));
    }
  }, [prompts, combo]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase() as ButtonKey;
      if (keys.includes(key)) {
        handleKeyPress(key);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyPress]);

  const endGame = () => {
    const accuracy = totalPrompts > 0 ? hits / totalPrompts : 0;
    
    let personalityBonus = 1;
    if (personality === 'showstopper') {
      personalityBonus = 1.2;
    }

    const finalHype = crowdHype * personalityBonus;
    let multiplier = 1;
    if (finalHype > 80) multiplier = 1.5;
    else if (finalHype > 60) multiplier = 1.2;
    else if (finalHype < 40) multiplier = 0.8;

    onComplete({
      score: Math.round(score * personalityBonus),
      multiplier,
      success: accuracy > 0.5
    });
  };

  return (
    <MiniGameWrapper
      title="Live Performance"
      instructions="Hit the keys when prompts reach the target zone!"
      timeLeft={timeLeft}
      maxTime={maxTime}
      onExit={onExit}
    >
      <div className="space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-2">
          <div className="p-2 bg-card/50 rounded border border-border text-center">
            <div className="text-xs text-muted-foreground">Score</div>
            <div className="text-xl font-bold text-primary">{score}</div>
          </div>
          <div className="p-2 bg-card/50 rounded border border-border text-center">
            <div className="text-xs text-muted-foreground">Combo</div>
            <div className="text-xl font-bold text-yellow-400">{combo}x</div>
          </div>
          <div className="p-2 bg-card/50 rounded border border-border text-center">
            <div className="text-xs text-muted-foreground">Crowd</div>
            <div className="text-xl font-bold text-pink-400">{crowdHype}%</div>
          </div>
        </div>

        {/* Crowd Hype Bar */}
        <div className="relative h-3 bg-muted rounded-full overflow-hidden">
          <motion.div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-pink-500 to-purple-500"
            initial={{ width: '50%' }}
            animate={{ width: `${crowdHype}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        {/* Game Area */}
        <div className="relative h-64 bg-gradient-to-b from-purple-900/20 to-pink-900/20 rounded-lg border border-border overflow-hidden">
          {/* Falling Prompts */}
          <AnimatePresence>
            {prompts.map(prompt => {
              const keyIndex = keys.indexOf(prompt.key);
              const laneWidth = 100 / keys.length;
              const leftPos = keyIndex * laneWidth + laneWidth / 2 - 8;
              
              return (
                <motion.div
                  key={prompt.id}
                  className="absolute w-16 h-16 flex items-center justify-center bg-primary/80 rounded-lg border-2 border-primary-foreground shadow-lg"
                  initial={{ top: 0, left: `${leftPos}%` }}
                  animate={{ top: '75%' }}
                  transition={{ duration: 2, ease: 'linear' }}
                >
                  <span className="text-2xl font-bold text-primary-foreground">
                    {keyLabels[prompt.key].toUpperCase()}
                  </span>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {/* Target Zone */}
          <div className="absolute bottom-16 left-0 right-0 h-20 border-y-2 border-dashed border-yellow-400/50 bg-yellow-400/5" />
        </div>

        {/* Key Buttons */}
        <div className="grid grid-cols-4 gap-2">
          {keys.map(key => (
            <motion.button
              key={key}
              className={`py-4 rounded-lg font-bold text-lg border-2 transition-colors ${
                activeKey === key
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border text-foreground'
              }`}
              onClick={() => handleKeyPress(key)}
              whileTap={{ scale: 0.95 }}
            >
              {keyLabels[key].toUpperCase()}
            </motion.button>
          ))}
        </div>
      </div>
    </MiniGameWrapper>
  );
};

export default PerformanceGame;
